<template>
	<div class="general-info mh-700">
		<v-layout row wrap>
			<v-flex xs12>
				<ViewHeader
					src="https://firebasestorage.googleapis.com/v0/b/icbem2020-c618e.appspot.com/o/images%2Fsingle-balloon-h800.jpg?alt=media&token=e78cb917-f9e7-49e2-a53e-4c07b43907d0"
					title="General Information"
				/>
			</v-flex>
		</v-layout>

		<v-layout row wrap class="padded">
			<v-flex xs12>
				<div class="info-section-header">
					<h3>Overview</h3>
				</div>
				<p>
					Registration and abstract submission will be done through
					your
					<a class="normal-link" @click="navigateToAccount"
						>user account profile</a
					>
					on this site after registration opens on March 1, 2022. All
					conferees need to register and pay the registration fee
					before submitting their abstract. The conference venue will
					be
					<strong
						>La Fonda on the Plaza, 100 East San Francisco Street,
						Santa Fe, New Mexico, USA 87501
					</strong>
					(<a
						class="normal-link"
						href="https://www.lafondasantafe.com/"
						>https://www.lafondasantafe.com/</a
					>). All rooms need to be booked directly with La Fonda.
				</p>
				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'head-side-mask']"
						class="venue-icon"
					/>
					<h3>COVID-19 Guidelines and Precautions</h3>
				</div>
				<p>
					Conference organizers cannot predict future outbreaks of
					COVID-19 nor anticipate local, state, and international
					regulations for travel and conferences. We recommend that
					anyone planning to attend ICBEM in Santa Fe in person obtain
					approved COVID-19 vaccinations and is prepared to document
					their vaccination status. We suggest you review the federal
					requirements at the Centers for Disease Control and
					Prevention
					<a
						class="normal-link"
						href="https://www.cdc.gov/coronavirus/2019-ncov/travelers/index.html"
						>website</a
					>.
				</p>

				<p>
					Also note that most universities in New Mexico currently (as
					of September 2021) require vaccinations and documentation,
					and the New Mexico Department of Health may issue
					regulations related to conferences, including wearing face
					masks in all public indoor spaces. You can review the
					current New Mexico public health orders at this
					<a
						class="normal-link"
						href="https://cv.nmhealth.org/public-health-orders-and-executive-orders/"
						>website</a
					>.
				</p>

				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'award']"
						class="venue-icon"
					/>
					<h3>Student/fellow travel award application procedure</h3>
				</div>
				<p class="text-red">
					The ICBEM student/postdoctoral fellow travel 
					award application deadline of March 31, 2022 is  closed, and 
					applications will be reviewed and awards made as appropriate, 
					with notifications made by early May.
				</p>
				<p class="text-red">
					
						Due to covid-19 and other international issues, travel 
						award application will continue to be accepted until May 
						31, 2022. This second group of applications will then be 
						reviewed and awards made from the remaining available funds.
					
				</p>
				<p>
					Up to 20 travel bursaries of about USD $1000 will be awarded
					to graduate students and postdocs on a competitive basis to
					participate in ICBEM 2022. Applicants for travel awards must
					submit their abstract and a cover letter that identifies
					their status (student or postdoc, year,
					lab/institution/mentor, and whether you previously received
					an ICBEM travel award) to the conference email address (<a
						href="mailto://icbem2020@gmail.com"
						>icbem2020@gmail.com</a
					>) between
					<strong>February 1, 2022 and March 31, 2022</strong>.
					Registration and fee payment are not required to apply for a
					travel award application. The receipt of the application
					will be confirmed by email.
				</p>
				<p>
					Please review the abstract instructions detailed below.
					Applicants must be the first author on the abstract and
					present a poster based on the abstract at the conference.
					Selected awardees will give 8-10 min oral presentations of
					their work. Conferees who have never received an ICBEM
					travel award will have priority over conferees who have
					previously been awarded an ICBEM travel award.
				</p>
				<p>
					Awards will be based on review scores of all abstracts,
					along with geography, sex, and other considerations (e.g.,
					no more than one award per lab group), and will be announced
					to applicants no later than the end of May 2022. Those
					receiving the awards must then register via the conference
					website (<a class="normal-link" href="/"
						>https://icbem2020.org/</a
					>), including payment of the registration fee and submission
					of the same abstract provided for the consideration of the
					travel award.
				</p>
				<p>
					The awards will be made at or after the conference,
					depending on the source(s) of funds and their specific
					requirements. Applicants who do not receive travel awards
					are welcome and encouraged to attend the conference. Those
					who plan to attend regardless of the outcome of their travel
					award application should register and submit their abstract
					before the registration deadline (in addition to sending the
					abstract for award consideration) at the conference website.
				</p>

				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'file']"
						class="venue-icon"
					/>
					<h3>Abstract Instructions</h3>
				</div>
				<p>
					English is the official language of the conference.
				</p>
				<p>
					<strong>Title: </strong>Use sentence case with the first
					word capitalized and all other words lower case.
				</p>
				<p>
					<strong>Authors: </strong>The presenting author must be the
					first author. All authors who contributed to the work must 
					be listed, however affiliations need only be given for the 
					primary author.
				</p>
				<p>
					<strong>Abstract: </strong> Please use single spacing and
					limit the length of the body of the abstract to 300 words.
					Non-standard abbreviations must be defined at first use with
					the full name spelled out followed by the abbreviations in
					parentheses. Indicate the source(s) of research support in a
					sentence at the end of the abstract. Do not include graphs
					or images.
				</p>
				<p>
					Abstracts are required for all speakers and poster
					presenters. Abstracts must be submitted
					<strong>no later than July 31, 2022</strong> (speaker
					abstracts are due by that date) for inclusion in the
					conference program book. After that date, poster presenters
					can still submit abstracts, with their inclusion as an
					insert.
				</p>
				<p>
					Poster presenters will participate in the “poster power pitch" session (3:30 to 5 pm, Tuesday,
					October 25, 2022) that precedes the two poster sessions at 5-7 pm and 8-10 pm. Each poster
					pitch talk will be limited to 3 min (2-3 slides) and should emphasize the major research
					results/highlights so conferees come to the poster for more detailed discussions.
				</p>
				<p>
					Poster set up is 2-6 pm, Monday, October 24, power pitch slides must be uploaded to the
					conference computer during this time interval. Foam-core posterboards are about 6 x 4 feet and
					accommodate the usual poster size of 4 x 3 feet (48 x 36 inches), but sizes up to about 6 x 4 feet
					size will fit. Posters can be either portrait or landscape orientation. Posters will be attached 
					to the boards with clamps or tacks. The conference will provide accessories, but it is best if 
					presenters to bring tacks or clamps for their posters.
				</p>

				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'plane']"
						class="venue-icon"
					/>
					<h3>Transportation - Air & Ground</h3>
				</div>
				<p>
					<a
						class="normal-link"
						href="https://www.abqsunport.com/"
						target="_blank"
						>Albuquerque International Sunport</a
					>
				</p>
				<p>
					The Albuquerque International Sunport is the main airport in
					the Santa Fe region. Shuttles can be taken between
					Albuquerque and Santa Fe, along with the New Mexico Rail
					Runner. You can get more information from this
					<a
						class="normal-link"
						href="https://firebasestorage.googleapis.com/v0/b/icbem2020-c618e.appspot.com/o/docs%2FAlbuquerqueInternationalSunport.pdf?alt=media&token=c3a1cc34-0add-4a8a-929f-da66932bd304"
						>PDF</a
					>.
				</p>
				<p>
					<a
						class="normal-link"
						href="http://www.santafenm.gov/airport"
						target="_blank"
						>Santa Fe Municipal Airport</a
					>
				</p>
				<p>
					You can get more information about Santa Fe Municipal
					Airport
					<a
						class="normal-link"
						href="https://firebasestorage.googleapis.com/v0/b/icbem2020-c618e.appspot.com/o/docs%2FSanta%20Fe%20Municipal%20Airport.pdf?alt=media&token=fc3d14df-30e3-4dfe-9404-b452a4230352"
						>here</a
					>.
				</p>
				<p>
					<a
						class="normal-link"
						href="https://www.riometro.org/395/New-Mexico-Rail-Runner-Express"
						target="_blank"
						>New Mexico Rail Runner</a
					>
				</p>
				<p>
					The New Mexico Rail Runner is a train service that runs
					between Santa Fe, Albuquerque, and points south. You can
					take a shuttle or taxi to the Albuquerque Train Station to
					get a train north.
				</p>
				<p>
					<a class="normal-link" href></a>
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'bus']"
						class="venue-icon-small"
					/>
					Shuttle Services
				</h4>
				<p>
					<a
						class="normal-link"
						href="https://groometransportation.com/santa-fe/?&sd_client_id=e2f4eaa8-de7b-4b9f-81b6-10637fd12e2b"
						target="_blank"
						>Groome Transportation</a
					>
				</p>

				<h4>
					<font-awesome-icon
						:icon="['fal', 'car-side']"
						class="venue-icon-small"
					/>
					Rental and Driving for Non-US-Citizens
				</h4>
				<p>
					Check with your rental car company for local requirements
					for driving. You will need an international drivers permit.
					Driving is on the right side of the road.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'map-marked-alt']"
						class="venue-icon-small"
					/>
					Driving Directions from I-25 and Albuquerque Airport
				</h4>
				<ol>
					<li>
						As you're leaving the airport, take the Sunport exit to
						I-25 North.
					</li>
					<li>Take Exit #284 (Old Pecos Trail).</li>
					<li>Turn left onto NM-466 N/Old Pecos Trail.</li>
					<li>
						Proceed North to the third traffic light, bear right to
						stay on Old Pecos Trail.
					</li>
					<li>Sight left onto Old Santa Fe Trail.</li>
					<li>Turn left on E Water Street.</li>
					<li>
						Turn Right on E San Francisco Street, the hotel will be
						on your right.
					</li>
					<li>
						Proceed to the hotel’s parking garage at the end of the
						block, on the right side of the street.
					</li>
				</ol>

				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'luggage-cart']"
						class="venue-icon"
					/>
					<h3>General Travel Information</h3>
				</div>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'passport']"
						class="venue-icon-small"
					/>Passports & Visas
				</h4>
				<p>
					Passports are required for entry into the US and must be
					valid for at least 6 months beyond your arrival date, unless
					your home country is exempt. Check to see if a visa is
					required for you.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'id-badge']"
						class="venue-icon-small"
					/>
					US Customs & Border Patrol
				</h4>
				<p>
					For details regarding customs allowances etc. go to
					<a class="normal-link" href="https://www.cbp.gov/"
						>the CPB websites</a
					>.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'usd-circle']"
						class="venue-icon-small"
					/>
					Language & Currency
				</h4>
				<p>
					The primary language is English, and the currency is the US
					dollar. Most banks will have ATMs.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'receipt']"
						class="venue-icon-small"
					/>Tipping
				</h4>
				<p>
					The US has a culture of tipping, typically 15-20% of the
					total bill for restaurants, and $1/bag for bellhops or hotel
					staff.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'bolt']"
						class="venue-icon-small"
					/>Electricity
				</h4>
				<p>
					AC voltage for standard US outlets is 110-120 volts. A
					voltage converter or universal adapter will be required to
					operate electrical devices from most other countries.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'ambulance']"
						class="venue-icon-small"
					/>Health Care & Insurance
				</h4>
				<p>
					Check with your provider to be sure you are covered while
					traveling. If not, consider supplemental travel health
					insurance. Find out in advance if your insurance plan will
					make payments directly to providers or reimburse you later
					for overseas health expenditures.
				</p>
				<p>
					<a
						class="normal-link"
						href="https://www.healthgrades.com/hospital-directory/nm-new-mexico/santa-fe"
						>Santa Fe Area Hospitals</a
					>
				</p>
				<p>
					<a
						class="normal-link"
						href="https://www.yelp.com/search?find_desc=emergency+room&find_loc=Santa+Fe%2C+NM"
						>Santa Fe Emergency Services</a
					>
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'sun-cloud']"
						class="venue-icon-small"
					/>Weather
				</h4>
				<p>
					Santa Fe has a semi-arid climate with an average of 325
					sunny days per year.<br />

					Santa Fe’s 7,000 feet (2,134m) altitude and low humidity
					means travelers need to acclimatize and keep hydrated. High
					altitude means high UV index, and good sunglasses and a brim
					hat are important, as is sun protection cream.<br />

					The average high / low temperatures in Santa Fe in October
					are 66° / 36°F (19 / 2.2°C), and in November 52° / 25° F (11
					/ -4°C).<br />

					In the October-November, a parka or jacket should be fine to
					keep warm during the day with more insulation (hat, gloves)
					for the lower evening/overnight temperatures. Comfortable
					walking shoes (sneakers, lightweight hiking shoes) are
					recommended, especially for those who plan side trips.
				</p>
				<h4>
					<font-awesome-icon
						:icon="['fal', 'clock']"
						class="venue-icon-small"
					/>Time Zone
				</h4>
				<p>
					Santa Fe is located in the Mountain Time zone (MDT).
					Daylight Savings will be in effect in October (GMT-6).
				</p>

				<div class="info-section-header">
					<font-awesome-icon
						:icon="['fal', 'umbrella-beach']"
						class="venue-icon"
					/>
					<h3>Area Tourism</h3>
				</div>
				<p>
					<a
						class="normal-link"
						href="https://www.santafe.org/visiting-santa-fe/"
						target="_blank"
						>Santa Fe Tourism Information</a
					>
				</p>
			</v-flex>
		</v-layout>
	</div>
</template>
<script>
import ViewHeader from '@/components/ViewHeader.vue';
export default {
	name: 'GeneralInformation',
	components: {
		ViewHeader
	},
	methods: {
		navigateToAccount: function() {
			this.$router.push('/account').catch(() => { });
		}
	}
};
</script>
<style src="../style.css"></style>
